import { useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, Package, FileText, IndianRupee, Settings,
  HelpCircle, Menu, Moon, Sun, LogOut, Plus, ChevronLeft,
  FileSpreadsheet, ClipboardList, FileCheck, ShoppingBag, Receipt,
  Truck, FileMinus, BarChart3, UsersRound, Landmark, HardHat, Scale,
  CheckCheck, Repeat, FolderOpen, Monitor, GraduationCap,
} from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import Walkthrough from "./Walkthrough";
import { useAuth } from "../context/AuthContext";
import { useCompany } from "../context/CompanyContext";
import { CompanySwitcher } from "./CompanySwitcher";
import { NotificationsBell } from "./NotificationsBell";
import { GlobalSearch } from "./GlobalSearch";
import { Button } from "./ui/button";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "./ui/dropdown-menu";
import { Avatar, AvatarFallback } from "./ui/avatar";

const NAV = [
  { section: "Main", items: [{ to: "/", label: "Dashboard", icon: LayoutDashboard, testId: "nav-dashboard" }] },
  { section: "General", items: [
    { to: "/parties", label: "Parties", icon: Users, testId: "nav-parties" },
    { to: "/items", label: "Items & Inventory", icon: Package, testId: "nav-items" },
  ]},
  { section: "Sales", items: [
    { to: "/invoices", label: "Sales Invoices", icon: FileText, testId: "nav-invoices" },
    { to: "/quotations", label: "Quotations", icon: FileSpreadsheet, testId: "nav-quotations" },
    { to: "/sales-orders", label: "Sales Orders", icon: ClipboardList, testId: "nav-sales-orders" },
    { to: "/proformas", label: "Proforma Invoices", icon: FileCheck, testId: "nav-proformas" },
    { to: "/delivery", label: "Delivery Challans", icon: Truck, testId: "nav-delivery" },
    { to: "/credit-notes", label: "Credit Notes", icon: FileMinus, testId: "nav-credit-notes" },
    { to: "/recurring", label: "Recurring Invoices", icon: Repeat, testId: "nav-recurring" },
  ]},
  { section: "Purchasing", items: [
    { to: "/purchases", label: "Purchases", icon: ShoppingBag, testId: "nav-purchases" },
  ]},
  { section: "Finance & Accounting", items: [
    { to: "/payments", label: "Payments & Ledger", icon: IndianRupee, testId: "nav-payments" },
    { to: "/cash-bank", label: "Cash & Bank", icon: Landmark, testId: "nav-cash-bank" },
    { to: "/expenses", label: "Expenses", icon: Receipt, testId: "nav-expenses" },
    { to: "/approvals", label: "Approvals", icon: CheckCheck, testId: "nav-approvals" },
  ]},
  { section: "Commercial", items: [
    { to: "/work-orders", label: "Work Orders", icon: HardHat, testId: "nav-work-orders" },
    { to: "/rate-comparison", label: "Rate Comparison", icon: Scale, testId: "nav-rate-comparison" },
  ]},
  { section: "HR & Admin", items: [
    { to: "/hr", label: "HR & Payroll", icon: UsersRound, testId: "nav-hr" },
    { to: "/assets", label: "Assets & Conveyance", icon: Monitor, testId: "nav-assets" },
  ]},
  { section: "Documents", items: [
    { to: "/documents", label: "Document Manager", icon: FolderOpen, testId: "nav-documents" },
  ]},
  { section: "Insights", items: [
    { to: "/reports", label: "Reports Center", icon: BarChart3, testId: "nav-reports" },
  ]},
  { section: "Governance", items: [
    { to: "/help", label: "Help & Learning", icon: HelpCircle, testId: "nav-help" },
    { to: "/settings", label: "Settings", icon: Settings, testId: "nav-settings" },
  ]},
];

function NavItems({ collapsed, onNavigate }) {
  const loc = useLocation();
  const nav = useNavigate();
  const go = (to) => { nav(to); onNavigate?.(); };
  return (
    <nav className="space-y-4 px-3 py-4">
      {NAV.map((sec) => (
        <div key={sec.section}>
          {!collapsed && (
            <p className="mb-1.5 px-2 text-[10px] font-semibold uppercase tracking-widest text-slate-400">{sec.section}</p>)}
          <div className="space-y-0.5">
            {sec.items.map((it) => {
              const activeNav = it.to === "/" ? loc.pathname === "/" : loc.pathname.startsWith(it.to);
              return (
                <button key={it.to} data-testid={it.testId} onClick={() => go(it.to)} title={it.label}
                  className={`flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm transition-colors ${
                    activeNav
                      ? "bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900 font-medium"
                      : "text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
                  } ${collapsed ? "justify-center" : ""}`}>
                  <it.icon className="h-4 w-4 shrink-0" />
                  {!collapsed && <span className="truncate">{it.label}</span>}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </nav>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const { active, activeId, role } = useCompany();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dark, setDark] = useState(() => localStorage.getItem("kg_theme") === "dark");
  const nav = useNavigate();

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("kg_theme", dark ? "dark" : "light");
  }, [dark]);

  const enterTraining = async () => {
    try {
      if (activeId) localStorage.setItem("kg_prev_company", activeId);
      const { data } = await api.post("/training/enter");
      localStorage.setItem("kg_company", data.company_id);
      toast.success("Training sandbox ready — sample data loaded");
      window.location.href = "/";
    } catch (e) { toast.error(errMsg(e)); }
  };

  const exitTraining = async () => {
    try {
      await api.post("/training/exit");
      const prev = localStorage.getItem("kg_prev_company");
      if (prev) localStorage.setItem("kg_company", prev);
      else localStorage.removeItem("kg_company");
      localStorage.removeItem("kg_prev_company");
      toast.success("Training mode ended — sandbox wiped");
      window.location.href = "/";
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#0B0F17]">
      <header className="app-chrome sticky top-0 z-40 flex h-16 items-center justify-between gap-3 border-b border-slate-200 dark:border-slate-800 bg-white/90 dark:bg-slate-900/90 px-4 sm:px-6 backdrop-blur-md">
        <div className="flex items-center gap-2">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button data-testid="mobile-menu-btn" variant="ghost" size="icon" className="lg:hidden h-9 w-9">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent
              side="left"
              className="w-64 p-0 bg-white dark:bg-slate-900 flex flex-col overflow-hidden"
            >
              <div className="flex h-16 min-h-16 shrink-0 items-center border-b border-slate-200 dark:border-slate-800 px-4">
                <span className="font-display font-bold">Krishna Group</span>
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain touch-pan-y">
                <NavItems onNavigate={() => setMobileOpen(false)} />
              </div>
            </SheetContent>
          </Sheet>
          <div className="hidden lg:flex items-center gap-2 mr-1">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-900 dark:bg-slate-100 text-amber-400 dark:text-amber-600 font-display font-extrabold text-sm">K</span>
            <div className="leading-tight">
              <p className="font-display font-bold text-sm">Krishna Group</p>
              <p className="text-[10px] uppercase tracking-widest text-slate-400">Business Management</p>
            </div>
          </div>
          <CompanySwitcher />
        </div>
        <div className="flex items-center gap-1.5">
          <GlobalSearch />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button data-testid="quick-action-btn" size="sm" className="hidden sm:flex h-9 gap-1.5 bg-slate-900 hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900">
                <Plus className="h-4 w-4" /> New
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700">
              <DropdownMenuItem data-testid="quick-new-invoice" onClick={() => nav("/invoices/new")} className="cursor-pointer">
                <FileText className="mr-2 h-4 w-4" /> Sales Invoice
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="quick-new-payment" onClick={() => nav("/payments?new=1")} className="cursor-pointer">
                <IndianRupee className="mr-2 h-4 w-4" /> Record Payment
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="quick-new-party" onClick={() => nav("/parties?new=1")} className="cursor-pointer">
                <Users className="mr-2 h-4 w-4" /> Party
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button data-testid="help-header-btn" variant="ghost" size="icon" className="h-9 w-9" onClick={() => nav("/help")}>
            <HelpCircle className="h-5 w-5" />
          </Button>
          <NotificationsBell />
          <Button data-testid="theme-toggle" variant="ghost" size="icon" className="h-9 w-9" onClick={() => setDark(!dark)}>
            {dark ? <Sun className="h-4.5 w-4.5 h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="user-menu-trigger" className="ml-1">
                <Avatar className="h-8 w-8">
                  <AvatarFallback className="bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 text-xs font-bold">
                    {(user?.name || "U").split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-60 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700">
              <DropdownMenuLabel>
                <p className="text-sm font-semibold">{user?.name}</p>
                <p className="text-xs text-slate-500 font-normal">{user?.email}</p>
                <p className="text-xs font-normal mt-1">
                  <span className="rounded bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-slate-500">
                    {role?.replace(/_/g, " ")} · {active?.code}
                  </span>
                </p>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {!active?.is_training && (
                <DropdownMenuItem data-testid="enter-training-btn" onClick={enterTraining} className="cursor-pointer">
                  <GraduationCap className="mr-2 h-4 w-4" /> Training Mode
                </DropdownMenuItem>
              )}
              <DropdownMenuItem data-testid="user-settings" onClick={() => nav("/settings")} className="cursor-pointer">
                <Settings className="mr-2 h-4 w-4" /> Settings
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="logout-btn" onClick={logout} className="cursor-pointer text-red-600 dark:text-red-400">
                <LogOut className="mr-2 h-4 w-4" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <div className="flex">
        <aside className={`app-chrome sticky top-16 hidden lg:flex h-[calc(100vh-4rem)] flex-col border-r border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 transition-all duration-300 ${collapsed ? "w-[68px]" : "w-60"}`}>
          <div className="min-h-0 flex-1 overflow-y-auto">
            <NavItems collapsed={collapsed} />
          </div>
          <div className="shrink-0 border-t border-slate-100 dark:border-slate-800 p-2">
            <button data-testid="sidebar-collapse-btn" onClick={() => setCollapsed(!collapsed)}
              className="flex w-full items-center justify-center rounded-lg p-2 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800">
              <ChevronLeft className={`h-4 w-4 transition-transform ${collapsed ? "rotate-180" : ""}`} />
            </button>
          </div>
        </aside>
        <main className="flex-1 p-4 sm:p-6 min-w-0">
          {active?.is_training && (
            <div data-testid="training-banner"
              className="mb-4 flex flex-wrap items-center gap-3 rounded-xl border border-amber-300 dark:border-amber-500/40 bg-amber-50 dark:bg-amber-500/10 px-4 py-2.5">
              <GraduationCap className="h-4 w-4 text-amber-600 shrink-0" />
              <p className="flex-1 text-xs sm:text-sm text-amber-800 dark:text-amber-300">
                <b>Training Mode</b> — you're in a private sandbox loaded with sample data. Experiment freely:
                everything here is wiped on exit and real company data is never touched.
              </p>
              <Button size="sm" variant="outline" data-testid="exit-training-btn" onClick={exitTraining}
                className="h-8 border-amber-400 text-amber-700 dark:text-amber-300">Exit Training Mode</Button>
            </div>
          )}
          <Outlet />
        </main>
      </div>
      <Walkthrough />
    </div>
  );
}
